import { computed, inject, Injectable, signal } from '@angular/core';
import { TemplateController } from '@domain';
import { environment } from '@environments';
import { AuthController } from '@domain';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Modo modelo do editor.
 *
 * Editar um modelo é editar um DOCUMENTO DE RASCUNHO criado pela API com o conteúdo dele
 * (`POST template/:id/draft`) — é o que permite reaproveitar o editor inteiro, que fala
 * `documentId` em todas as rotas. A rota do editor é a de sempre; o que liga o modo modelo é
 * o parâmetro `?templateId=` na URL, lido pela `EditPage`.
 *
 * Não há "salvar" nem "descartar": cada edição é gravada no rascunho pelo salvamento
 * automático de sempre, e ao SAIR do editor o rascunho é sincronizado com o modelo
 * (`syncOnLeave`). Quem fecha a aba no meio é coberto pelo worker da API. Enquanto o modo
 * está ligado, o editor esconde o que é do documento (compartilhar, assinar, PDF, mover,
 * duplicar, excluir).
 */
@Injectable({ providedIn: 'root' })
export class TemplateEditService {
    /** ID do MODELO em edição (não do rascunho). `null` = editor em modo documento normal. */
    public templateId = signal<string | null>(null);
    public templateName = signal<string>('');

    public isTemplateMode = computed(() => !!this.templateId());

    // * [Injects]
    private _authController = inject(AuthController);
    private _templateController = inject(TemplateController);

    /**
     * Liga o modo modelo. O nome pode chegar pronto de quem navegou (a lista de modelos já
     * tem) e, no refresh da página, é buscado — a URL guarda só o ID.
     */
    public start(templateId: string, name?: string): void {
        this.templateId.set(templateId);
        this.templateName.set(name ?? '');

        if (!name) this._loadTemplateName(templateId);
    }

    public clear(): void {
        this.templateId.set(null);
        this.templateName.set('');
    }

    /**
     * Sincroniza o rascunho com o modelo ao sair do editor. Sempre completa (erro vira
     * toast e `false`): quem chama só precisa esperar para a lista de modelos já abrir
     * atualizada.
     */
    public syncOnLeave(): Observable<boolean> {
        const templateId = this.templateId();
        if (!templateId) return of(false);

        return this._templateController.syncDraft(templateId).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    /**
     * Mesma sincronização, para o fechamento da aba: `keepalive` faz a request sobreviver
     * ao descarregamento da página. Sem retorno — se não chegar, o worker da API cobre.
     */
    public syncWithKeepalive(): void {
        const templateId = this.templateId();
        if (!templateId) return;

        const token = this._authController.getTokenInCookie();
        const url = `${environment.API_BASE}template/${templateId}/draft/sync`;

        fetch(url, {
            method: 'POST',
            headers: {
                ...(token && { Authorization: token }),
                'Content-Type': 'application/json',
            },
            body: '{}',
            keepalive: true,
        }).catch(() => {
            // A página já pode estar sendo descarregada
        });
    }

    private _loadTemplateName(templateId: string): void {
        this._templateController.getAll().subscribe({
            next: templates => {
                const template = templates?.find(item => item._id === templateId);
                if (template) this.templateName.set(template.title);
            },
        });
    }
}
